import React, { useEffect, useState, Component } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion, useScroll } from 'framer-motion';
import { ArrowLeft, ArrowRight, Clock, Heart, Bookmark, Share2, MessageCircle, Send, Twitter, Facebook, Linkedin, Copy, Check, Sparkles, Calendar } from 'lucide-react';
import { getPostBySlug, getRelatedPosts, blogPosts } from '../data/blogPosts';
interface Comment {
  id: number;
  name: string;
  date: string;
  text: string;
  avatar?: string;
  likes: number;
}
const initialComments: Comment[] = [{
  id: 1,
  name: 'Tunde Bakare',
  date: '2 days ago',
  text: 'This is the kind of analysis the Nigerian real estate space has been missing. Bookmarked.',
  likes: 12
}, {
  id: 2,
  name: 'Adaeze Nwosu',
  date: '5 days ago',
  text: 'Just bought into Tiwa Garden City based on similar logic 6 months ago. Already up 22%. Zylus Media is right on the money.',
  likes: 27
}, {
  id: 3,
  name: 'Femi A.',
  date: '1 week ago',
  text: 'Great piece. Would love to see a follow-up on the Epe corridor specifically.',
  likes: 8
}];
export function BlogPost() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const post = slug ? getPostBySlug(slug) : null;
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [copied, setCopied] = useState(false);
  const [comments, setComments] = useState<Comment[]>(initialComments);
  const [newComment, setNewComment] = useState('');
  const [commenterName, setCommenterName] = useState('');
  const {
    scrollYProgress
  } = useScroll();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!post) return <Navigate to="/blog" replace data-id="element-809" />;
  const related = getRelatedPosts(post.id, 3);
  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !commenterName.trim()) return;
    setComments([{
      id: Date.now(),
      name: commenterName,
      date: 'Just now',
      text: newComment,
      likes: 0
    }, ...comments]);
    setNewComment('');
    setCommenterName('');
  };
  const likeComment = (id: number) => {
    setComments(cs => cs.map(c => c.id === id ? {
      ...c,
      likes: c.likes + 1
    } : c));
  };
  // Split content for mid-article ad placement
  const contentMidpoint = Math.ceil(post.content.length / 2);
  const firstHalf = post.content.slice(0, contentMidpoint);
  const secondHalf = post.content.slice(contentMidpoint);
  return <div className="bg-bone" data-id="element-810">
      {/* Reading Progress Bar */}
      <motion.div style={{
      scaleX: scrollYProgress
    }} className="fixed top-0 left-0 right-0 h-1 bg-gold origin-left z-[60]" data-id="element-811" />

      {/* Hero */}
      <div className="relative h-[60vh] min-h-[480px] overflow-hidden" data-id="element-812">
        <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" data-id="element-813" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30" data-id="element-814" />

        <div className="absolute inset-0 flex items-end pb-20 pt-32" data-id="element-815">
          <div className="max-w-[1280px] mx-auto w-full px-6 md:px-12" data-id="element-816">
            <Link to="/blog" className="inline-flex items-center gap-2 text-bone/70 hover:text-gold text-xs font-bold uppercase tracking-widest mb-6 transition-colors" data-id="element-817">
              <ArrowLeft className="w-4 h-4" data-id="element-818" />
              Back to Zylus Media
            </Link>

            <div className="inline-flex items-center gap-2 bg-gold text-ink px-3 py-1.5 rounded-full text-[10px] font-bold tracking-widest uppercase mb-5" data-id="element-819">
              <Sparkles className="w-3 h-3" data-id="element-820" />
              {post.category}
            </div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-4xl md:text-5xl lg:text-6xl font-serif text-white max-w-4xl leading-[1.1] mb-6" data-id="element-821">
              {post.title}
            </motion.h1>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-bone/80" data-id="element-822">
              <div className="flex items-center gap-2" data-id="element-823">
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center text-ink font-serif font-bold text-sm" data-id="element-824">
                  Z
                </div>
                <div data-id="element-825">
                  <div className="font-semibold text-white" data-id="element-826">
                    {post.publisher}
                  </div>
                  <div className="text-[11px] text-bone/60" data-id="element-827">
                    by {post.author} · {post.authorRole}
                  </div>
                </div>
              </div>
              <span className="hidden sm:block w-1 h-1 rounded-full bg-bone/30" data-id="element-828" />
              <span className="flex items-center gap-2" data-id="element-829">
                <Calendar className="w-3.5 h-3.5" data-id="element-830" /> {post.date}
              </span>
              <span className="hidden sm:block w-1 h-1 rounded-full bg-bone/30" data-id="element-831" />
              <span className="flex items-center gap-2" data-id="element-832">
                <Clock className="w-3.5 h-3.5" data-id="element-833" /> {post.readTime}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-[1280px] mx-auto px-6 md:px-12 py-16" data-id="element-834">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-835">
          {/* Article */}
          <article className="lg:col-span-8" data-id="element-836">
            {/* Share / Like / Bookmark sticky bar */}
            <div className="flex items-center gap-2 mb-10 pb-8 border-b border-ink/10" data-id="element-837">
              <button onClick={() => setLiked(!liked)} className={`flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-semibold transition-all ${liked ? 'bg-gold border-gold text-ink' : 'bg-white border-ink/10 text-ink/70 hover:border-gold hover:text-ink'}`} data-id="element-838">
                <Heart className={`w-3.5 h-3.5 ${liked ? 'fill-ink' : ''}`} data-id="element-839" />
                {liked ? 'Liked' : 'Like'}
              </button>
              <button onClick={() => setBookmarked(!bookmarked)} className={`flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-semibold transition-all ${bookmarked ? 'bg-ink border-ink text-bone' : 'bg-white border-ink/10 text-ink/70 hover:border-ink hover:text-ink'}`} data-id="element-840">
                <Bookmark className={`w-3.5 h-3.5 ${bookmarked ? 'fill-bone' : ''}`} data-id="element-841" />
                {bookmarked ? 'Saved' : 'Save'}
              </button>

              <div className="ml-auto flex items-center gap-2" data-id="element-842">
                <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-white border border-ink/10 hover:bg-ink hover:text-white hover:border-ink flex items-center justify-center transition-colors" aria-label="Share on Twitter" data-id="element-843">
                  <Twitter className="w-4 h-4" data-id="element-844" />
                </a>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-white border border-ink/10 hover:bg-ink hover:text-white hover:border-ink flex items-center justify-center transition-colors" aria-label="Share on Facebook" data-id="element-845">
                  <Facebook className="w-4 h-4" data-id="element-846" />
                </a>
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-white border border-ink/10 hover:bg-ink hover:text-white hover:border-ink flex items-center justify-center transition-colors" aria-label="Share on LinkedIn" data-id="element-847">
                  <Linkedin className="w-4 h-4" data-id="element-848" />
                </a>
                <button onClick={handleCopy} className="w-9 h-9 rounded-full bg-white border border-ink/10 hover:bg-gold hover:border-gold flex items-center justify-center transition-colors" aria-label="Copy link" data-id="element-849">
                  {copied ? <Check className="w-4 h-4" data-id="element-850" /> : <Copy className="w-4 h-4" data-id="element-851" />}
                </button>
              </div>
            </div>

            {/* First half of content */}
            <div className="prose-zylus space-y-6 text-ink/85 text-lg leading-relaxed" data-id="element-852">
              {firstHalf.map((para, i) => <Paragraph key={i} text={para} data-id="element-853" />)}
            </div>

            {/* Pull Quote */}
            {post.pullQuote && <motion.blockquote initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="my-12 pl-8 border-l-4 border-gold relative" data-id="element-854">
                <div className="text-3xl md:text-4xl font-serif text-ink leading-snug italic" data-id="element-855">
                  "{post.pullQuote}"
                </div>
                <div className="mt-4 text-xs font-bold uppercase tracking-widest text-gold-dark" data-id="element-856">
                  — {post.publisher}
                </div>
              </motion.blockquote>}

            {/* Embedded Estate Ad */}
            <EstateAdvert data-id="element-857" />

            {/* Second half */}
            <div className="prose-zylus space-y-6 text-ink/85 text-lg leading-relaxed mt-10" data-id="element-858">
              {secondHalf.map((para, i) => <Paragraph key={i} text={para} data-id="element-859" />)}
            </div>

            {/* Tags */}
            <div className="flex flex-wrap items-center gap-2 mt-12 pt-8 border-t border-ink/10" data-id="element-860">
              <span className="text-[10px] font-bold uppercase tracking-widest text-ink/50 mr-2" data-id="element-861">
                Tags
              </span>
              {post.tags.map(tag => <span key={tag} className="bg-bone hover:bg-gold transition-colors text-ink text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer" data-id="element-862">
                  #{tag}
                </span>)}
            </div>

            {/* Author Card */}
            <div className="mt-12 bg-white rounded-3xl p-8 border border-ink/5 flex flex-col sm:flex-row gap-6 items-start" data-id="element-863">
              <div className="w-20 h-20 rounded-full bg-ink flex items-center justify-center text-white text-2xl font-serif shrink-0" data-id="element-864">
                Z
              </div>
              <div data-id="element-865">
                <div className="text-[10px] font-bold uppercase tracking-widest text-gold-dark mb-1" data-id="element-866">
                  Published by
                </div>
                <h4 className="text-2xl font-serif text-ink mb-1" data-id="element-867">
                  {post.publisher}
                </h4>
                <div className="text-sm text-ink/60 mb-3" data-id="element-868">
                  Editorial — {post.author}, {post.authorRole}
                </div>
                <p className="text-sm text-ink/70 leading-relaxed" data-id="element-869">
                  Zylus Media is the editorial arm of Zylus Homes & Property —
                  covering African real estate, investment intelligence, and
                  proptech innovation.
                </p>
              </div>
            </div>

            {/* Comments */}
            <section className="mt-16" data-id="element-870">
              <div className="flex items-center justify-between mb-8" data-id="element-871">
                <h3 className="text-2xl font-serif text-ink flex items-center gap-3" data-id="element-872">
                  <MessageCircle className="w-6 h-6 text-gold" data-id="element-873" />
                  {comments.length} Comments
                </h3>
              </div>

              {/* New Comment Form */}
              <form onSubmit={handleSubmitComment} className="bg-white rounded-3xl p-6 border border-ink/5 mb-8 space-y-4" data-id="element-874">
                <input type="text" placeholder="Your name" value={commenterName} onChange={e => setCommenterName(e.target.value)} className="w-full bg-bone/40 border border-ink/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-gold transition-colors placeholder:text-ink/40" required data-id="element-875" />
                <textarea rows={3} placeholder="Share your thoughts..." value={newComment} onChange={e => setNewComment(e.target.value)} className="w-full bg-bone/40 border border-ink/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-gold transition-colors resize-none placeholder:text-ink/40" required data-id="element-876" />
                <button type="submit" className="bg-gold hover:bg-gold-dark text-ink font-bold uppercase tracking-widest text-xs px-6 py-3 rounded-xl transition-colors flex items-center gap-2" data-id="element-877">
                  Post Comment <Send className="w-3.5 h-3.5" data-id="element-878" />
                </button>
              </form>

              {/* Comments list */}
              <div className="space-y-5" data-id="element-879">
                {comments.map(c => <motion.div key={c.id} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="bg-white rounded-2xl p-6 border border-ink/5 flex gap-4" data-id="element-880">
                    <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center text-ink font-serif font-semibold shrink-0" data-id="element-881">
                      {c.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-882">
                      <div className="flex items-center gap-3 mb-2" data-id="element-883">
                        <span className="font-semibold text-ink text-sm" data-id="element-884">
                          {c.name}
                        </span>
                        <span className="text-xs text-ink/40" data-id="element-885">{c.date}</span>
                      </div>
                      <p className="text-ink/80 text-sm leading-relaxed mb-3" data-id="element-886">
                        {c.text}
                      </p>
                      <button onClick={() => likeComment(c.id)} className="flex items-center gap-1.5 text-xs text-ink/50 hover:text-gold transition-colors" data-id="element-887">
                        <Heart className="w-3.5 h-3.5" data-id="element-888" /> {c.likes}
                      </button>
                    </div>
                  </motion.div>)}
              </div>
            </section>
          </article>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-32 self-start" data-id="element-889">
            {/* Featured Estate Sidebar Ad */}
            <div className="bg-ink rounded-3xl overflow-hidden text-white relative" data-id="element-890">
              <div className="h-48 relative overflow-hidden" data-id="element-891">
                <img src="https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=600&q=80" alt="Zylus Court Phase II" className="w-full h-full object-cover" data-id="element-892" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink to-transparent" data-id="element-893" />
                <div className="absolute top-4 left-4 bg-gold text-ink text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full" data-id="element-894">
                  Sponsored
                </div>
              </div>
              <div className="p-6" data-id="element-895">
                <div className="text-[10px] font-bold tracking-widest uppercase text-gold mb-2" data-id="element-896">
                  Featured Estate
                </div>
                <h4 className="font-serif text-xl mb-2" data-id="element-897">
                  Zylus Court Phase II
                </h4>
                <p className="text-white/60 text-sm mb-5" data-id="element-898">
                  Premium smart homes in Bogije, Ibeju-Lekki. Starting from ₦85M
                  with flexible payment plans.
                </p>
                <Link to="/houses" className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-ink font-semibold text-xs uppercase tracking-widest px-4 py-2.5 rounded-full transition-colors w-full justify-center" data-id="element-899">
                  View Estate <ArrowRight className="w-3.5 h-3.5" data-id="element-900" />
                </Link>
              </div>
            </div>

            {/* Newsletter */}
            <div className="bg-white rounded-3xl p-6 border border-ink/5" data-id="element-901">
              <div className="text-[10px] font-bold tracking-widest uppercase text-gold mb-2" data-id="element-902">
                Zylus Media Briefing
              </div>
              <h4 className="font-serif text-xl text-ink mb-3" data-id="element-903">
                Get weekly insights
              </h4>
              <p className="text-ink/60 text-sm mb-5" data-id="element-904">
                Off-market opportunities and editorial picks — straight to your
                inbox.
              </p>
              <form onSubmit={e => e.preventDefault()} className="space-y-3" data-id="element-905">
                <input type="email" placeholder="your@email.com" className="w-full bg-bone/40 border border-ink/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-gold transition-colors placeholder:text-ink/40" data-id="element-906" />
                <button type="submit" className="w-full bg-ink hover:bg-gold hover:text-ink text-white font-bold uppercase tracking-widest text-xs py-3 rounded-xl transition-colors" data-id="element-907">
                  Subscribe Free
                </button>
              </form>
            </div>

            {/* Trending */}
            <div className="bg-white rounded-3xl p-6 border border-ink/5" data-id="element-908">
              <h4 className="font-serif text-xl text-ink mb-5 flex items-center gap-2" data-id="element-909">
                <Sparkles className="w-4 h-4 text-gold" data-id="element-910" />
                Trending This Week
              </h4>
              <div className="space-y-4" data-id="element-911">
                {blogPosts.slice(1, 4).map((p, i) => <Link key={p.id} to={`/blog/${p.slug}`} className="flex gap-3 group" data-id="element-912">
                    <div className="text-3xl font-serif text-gold/30 leading-none w-8 shrink-0" data-id="element-913">
                      0{i + 1}
                    </div>
                    <div data-id="element-914">
                      <div className="text-sm text-ink font-medium leading-snug group-hover:text-gold transition-colors line-clamp-2" data-id="element-915">
                        {p.title}
                      </div>
                      <div className="text-[10px] text-ink/40 mt-1" data-id="element-916">
                        {p.readTime}
                      </div>
                    </div>
                  </Link>)}
              </div>
            </div>
          </aside>
        </div>

        {/* Related Articles */}
        <section className="mt-24" data-id="element-917">
          <div className="flex items-center justify-between mb-10" data-id="element-918">
            <h3 className="text-3xl font-serif text-ink" data-id="element-919">Continue Reading</h3>
            <Link to="/blog" className="text-sm text-ink/60 hover:text-gold flex items-center gap-2 font-medium" data-id="element-920">
              All articles <ArrowRight className="w-4 h-4" data-id="element-921" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-922">
            {related.map((p, i) => <motion.div key={p.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.08
          }} whileHover={{
            y: -6
          }} className="bg-white rounded-2xl overflow-hidden border border-ink/5 hover:shadow-xl transition-shadow group" data-id="element-923">
                <Link to={`/blog/${p.slug}`} className="block" data-id="element-924">
                  <div className="relative h-48 overflow-hidden" data-id="element-925">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-926" />
                  </div>
                  <div className="p-5" data-id="element-927">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gold-dark mb-2" data-id="element-928">
                      {p.category}
                    </div>
                    <h5 className="font-serif text-ink leading-snug mb-2 group-hover:text-gold transition-colors line-clamp-2" data-id="element-929">
                      {p.title}
                    </h5>
                    <div className="text-xs text-ink/50 flex items-center gap-2" data-id="element-930">
                      <Clock className="w-3 h-3" data-id="element-931" /> {p.readTime}
                    </div>
                  </div>
                </Link>
              </motion.div>)}
          </div>
        </section>
      </div>
    </div>;
}
// Renders a paragraph; supports **bold** segments and \n line breaks
function Paragraph({
  text
}: {
  text: string;
}) {
  // Split by \n to handle subheadings inline
  const lines = text.split('\n').filter(Boolean);
  return <>
      {lines.map((line, idx) => {
      // Detect subheading pattern (**Text**)
      const subheadingMatch = line.match(/^\*\*(.+?)\*\*$/);
      if (subheadingMatch) {
        return <h3 key={idx} className="text-2xl md:text-3xl font-serif text-ink mt-10 mb-2" data-id="element-932">
              {subheadingMatch[1]}
            </h3>;
      }
      // Inline bold parsing
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      return <p key={idx} data-id="element-933">
            {parts.map((part, pi) => {
          const m = part.match(/^\*\*(.+?)\*\*$/);
          if (m) return <strong key={pi} className="font-semibold text-ink" data-id="element-934">
                    {m[1]}
                  </strong>;
          return <span key={pi} data-id="element-935">{part}</span>;
        })}
          </p>;
    })}
    </>;
}
// Mid-article estate sponsor block
function EstateAdvert() {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="my-12 not-prose" data-id="element-936">
      <div className="text-[9px] font-bold tracking-[0.25em] uppercase text-ink/40 mb-2 text-center" data-id="element-937">
        — Sponsored —
      </div>
      <div className="relative bg-gradient-to-br from-ink to-ink/90 rounded-3xl overflow-hidden text-white grid grid-cols-1 md:grid-cols-2 gap-0" data-id="element-938">
        <div className="relative h-56 md:h-auto overflow-hidden" data-id="element-939">
          <img src="https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&q=80" alt="Tiwa Garden City" className="w-full h-full object-cover" data-id="element-940" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-ink/40" data-id="element-941" />
        </div>
        <div className="p-8 flex flex-col justify-center relative" data-id="element-942">
          <div className="absolute top-6 right-6 bg-gold text-ink text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full" data-id="element-943">
            Featured
          </div>
          <div className="text-[10px] font-bold tracking-widest uppercase text-gold mb-3" data-id="element-944">
            Now Selling
          </div>
          <h4 className="text-2xl md:text-3xl font-serif mb-3" data-id="element-945">
            Tiwa Garden City
          </h4>
          <p className="text-white/70 text-sm mb-6 leading-relaxed" data-id="element-946">
            A visionary estate on the Lekki-Epe Expressway. 5–6 bed homes from
            ₦120M with infrastructure already in place.
          </p>
          <div className="flex flex-wrap gap-3" data-id="element-947">
            <Link to="/houses" className="bg-gold hover:bg-gold-dark text-ink font-bold uppercase tracking-widest text-xs px-5 py-3 rounded-full transition-colors inline-flex items-center gap-2" data-id="element-948">
              Explore Estate <ArrowRight className="w-3.5 h-3.5" data-id="element-949" />
            </Link>
            <Link to="/#book" className="bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold uppercase tracking-widest text-xs px-5 py-3 rounded-full transition-colors" data-id="element-950">
              Book Inspection
            </Link>
          </div>
        </div>
      </div>
    </motion.div>;
}